import { Navigate } from "react-router-dom";
import PageTitle from "../page-header/PageHeader";
import {
	Card,
	Table,
	Tag,
	Statistic,
	Row,
	Col,
	Select,
	Progress,
	Alert,
	Space,
	Typography,
} from "antd";
import { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { loadAllStaff } from "../../redux/rtk/features/user/userSlice";
import {
	CheckCircleOutlined,
	WarningOutlined,
	ExclamationCircleOutlined,
	RiseOutlined,
	FallOutlined,
	TrophyOutlined,
	UserOutlined,
} from "@ant-design/icons";

const { Title, Text } = Typography;
const { Option } = Select;

const PerformanceInsights = () => {
	const dispatch = useDispatch();
	const isLogged = Boolean(localStorage.getItem("isLogged"));
	const staffList = useSelector((state) => state.users?.list || []);
	const [selectedPeriod, setSelectedPeriod] = useState("All");
	const [selectedDepartment, setSelectedDepartment] = useState("All");

	const [reviewsData, setReviewsData] = useState(() => {
		const stored = localStorage.getItem("performanceReviews");
		if (stored) {
			try {
				return JSON.parse(stored);
			} catch (error) {
				return [];
			}
		}
		return [];
	});

	const [feedbackData, setFeedbackData] = useState(() => {
		const stored = localStorage.getItem("performanceFeedback");
		if (stored) {
			try {
				return JSON.parse(stored);
			} catch (error) {
				return [];
			}
		}
		return [];
	});

	const [pipData, setPipData] = useState(() => {
		const stored = localStorage.getItem("performanceImprovementPlans");
		if (stored) {
			try {
				return JSON.parse(stored);
			} catch (error) {
				return [];
			}
		}
		return [];
	});

	useEffect(() => {
		dispatch(loadAllStaff({ status: true }));
	}, [dispatch]);

	// Calculate comprehensive insights
	const insights = useMemo(() => {
		const completedReviews = reviewsData.filter((r) => r.status === "Completed");
		const completedFeedback = feedbackData.filter((f) => f.status === "Completed");
		const activePIPs = pipData.filter((p) => p.status === "Active");
		const completedPIPs = pipData.filter((p) => p.status === "Completed");

		// Overall statistics
		const totalReviews = completedReviews.length;
		const totalFeedback = completedFeedback.length;
		const totalEmployees = staffList.length;

		// Average ratings
		const avgOverallRating =
			completedReviews.length > 0
				? completedReviews.reduce((sum, r) => sum + (r.overallRating || 0), 0) / completedReviews.length
				: 0;

		const avgFeedbackRating =
			completedFeedback.length > 0
				? completedFeedback.reduce((sum, f) => sum + (f.overallRating || 0), 0) / completedFeedback.length
				: 0;

		// Rating distribution
		const ratingDistribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
		completedReviews.forEach((review) => {
			const rating = Math.round(review.overallRating);
			if (rating >= 1 && rating <= 5) {
				ratingDistribution[rating]++;
			}
		});

		// Top performers
		const employeeRatings = {};
		completedReviews.forEach((review) => {
			const empId = review.employeeId;
			if (!employeeRatings[empId]) {
				employeeRatings[empId] = {
					employeeId: empId,
					employeeName: review.employeeName,
					ratings: [],
					averageRating: 0,
					totalReviews: 0,
				};
			}
			employeeRatings[empId].ratings.push(review.overallRating);
			employeeRatings[empId].totalReviews++;
		});

		Object.keys(employeeRatings).forEach((empId) => {
			const emp = employeeRatings[empId];
			emp.averageRating =
				emp.ratings.reduce((sum, r) => sum + r, 0) / emp.ratings.length;
		});

		const topPerformers = Object.values(employeeRatings)
			.sort((a, b) => b.averageRating - a.averageRating)
			.slice(0, 5);

		// Employees needing improvement
		const employeesNeedingImprovement = Object.values(employeeRatings)
			.filter((emp) => emp.averageRating < 3.0)
			.sort((a, b) => a.averageRating - b.averageRating)
			.slice(0, 5);

		// Performance by criteria
		const criteriaAverages = {
			jobKnowledge: 0,
			qualityOfWork: 0,
			productivity: 0,
			communication: 0,
			teamwork: 0,
			initiative: 0,
			problemSolving: 0,
		};

		completedReviews.forEach((review) => {
			criteriaAverages.jobKnowledge += review.jobKnowledge || 0;
			criteriaAverages.qualityOfWork += review.qualityOfWork || 0;
			criteriaAverages.productivity += review.productivity || 0;
			criteriaAverages.communication += review.communication || 0;
			criteriaAverages.teamwork += review.teamwork || 0;
			criteriaAverages.initiative += review.initiative || 0;
			criteriaAverages.problemSolving += review.problemSolving || 0;
		});

		const reviewCount = completedReviews.length;
		Object.keys(criteriaAverages).forEach((key) => {
			criteriaAverages[key] = reviewCount > 0 ? criteriaAverages[key] / reviewCount : 0;
		});

		// PIP statistics
		const pipSuccessRate =
			completedPIPs.length + activePIPs.length > 0
				? (completedPIPs.length / (completedPIPs.length + activePIPs.length)) * 100
				: 0;

		// Review completion rate
		const totalReviewsCreated = reviewsData.length;
		const reviewCompletionRate =
			totalReviewsCreated > 0 ? (completedReviews.length / totalReviewsCreated) * 100 : 0;

		// Performance trends (last 6 months)
		const now = dayjs();
		const monthlyTrends = [];
		for (let i = 5; i >= 0; i--) {
			const monthStart = now.subtract(i, "month").startOf("month");
			const monthEnd = now.subtract(i, "month").endOf("month");
			const monthReviews = completedReviews.filter((r) => {
				const reviewDate = dayjs(r.reviewDate);
				return reviewDate.isAfter(monthStart) && reviewDate.isBefore(monthEnd);
			});
			const monthAvg =
				monthReviews.length > 0
					? monthReviews.reduce((sum, r) => sum + (r.overallRating || 0), 0) / monthReviews.length
					: 0;
			monthlyTrends.push({
				month: monthStart.format("MMM YYYY"),
				average: monthAvg,
				count: monthReviews.length,
			});
		}

		return {
			totalReviews,
			totalFeedback,
			totalEmployees,
			avgOverallRating: avgOverallRating.toFixed(2),
			avgFeedbackRating: avgFeedbackRating.toFixed(2),
			ratingDistribution,
			topPerformers,
			employeesNeedingImprovement,
			criteriaAverages,
			pipSuccessRate: pipSuccessRate.toFixed(1),
			activePIPs: activePIPs.length,
			completedPIPs: completedPIPs.length,
			reviewCompletionRate: reviewCompletionRate.toFixed(1),
			monthlyTrends,
		};
	}, [reviewsData, feedbackData, pipData, staffList]);

	const topPerformersColumns = [
		{
			title: "Rank",
			key: "rank",
			render: (_, __, index) => (
				<Tag color={index === 0 ? "gold" : index === 1 ? "default" : index === 2 ? "orange" : "blue"}>
					#{index + 1}
				</Tag>
			),
		},
		{
			title: "Employee",
			dataIndex: "employeeName",
			key: "employeeName",
		},
		{
			title: "Average Rating",
			dataIndex: "averageRating",
			key: "averageRating",
			render: (rating) => (
				<Tag color={rating >= 4 ? "green" : rating >= 3 ? "blue" : "orange"}>
					{rating.toFixed(2)}/5
				</Tag>
			),
		},
		{
			title: "Total Reviews",
			dataIndex: "totalReviews",
			key: "totalReviews",
			align: "center",
		},
	];

	const improvementNeededColumns = [
		{
			title: "Employee",
			dataIndex: "employeeName",
			key: "employeeName",
		},
		{
			title: "Average Rating",
			dataIndex: "averageRating",
			key: "averageRating",
			render: (rating) => (
				<Tag color="red">
					{rating.toFixed(2)}/5
				</Tag>
			),
		},
		{
			title: "Total Reviews",
			dataIndex: "totalReviews",
			key: "totalReviews",
			align: "center",
		},
		{
			title: "Action",
			key: "action",
			render: (_, record) => {
				const hasPIP = pipData.some((p) => p.employeeId === record.employeeId && p.status === "Active");
				return hasPIP ? (
					<Tag color="orange">PIP Active</Tag>
				) : (
					<Tag color="default">No PIP</Tag>
				);
			},
		},
	];

	const criteriaColumns = [
		{
			title: "Performance Criteria",
			dataIndex: "criteria",
			key: "criteria",
		},
		{
			title: "Average Rating",
			dataIndex: "rating",
			key: "rating",
			render: (rating) => (
				<Space>
					<Progress
						percent={(rating / 5) * 100}
						format={() => `${rating.toFixed(2)}/5`}
						strokeColor={rating >= 4 ? "#52c41a" : rating >= 3 ? "#1890ff" : "#faad14"}
					/>
				</Space>
			),
		},
		{
			title: "Status",
			key: "status",
			render: (_, record) => {
				const rating = record.rating;
				if (rating >= 4) {
					return <Tag color="green" icon={<RiseOutlined />}>Excellent</Tag>;
				} else if (rating >= 3) {
					return <Tag color="blue">Good</Tag>;
				} else {
					return <Tag color="orange" icon={<WarningOutlined />}>Needs Improvement</Tag>;
				}
			},
		},
	];

	const criteriaData = [
		{ key: 1, criteria: "Job Knowledge", rating: insights.criteriaAverages.jobKnowledge },
		{ key: 2, criteria: "Quality of Work", rating: insights.criteriaAverages.qualityOfWork },
		{ key: 3, criteria: "Productivity", rating: insights.criteriaAverages.productivity },
		{ key: 4, criteria: "Communication", rating: insights.criteriaAverages.communication },
		{ key: 5, criteria: "Teamwork", rating: insights.criteriaAverages.teamwork },
		{ key: 6, criteria: "Initiative", rating: insights.criteriaAverages.initiative },
		{ key: 7, criteria: "Problem Solving", rating: insights.criteriaAverages.problemSolving },
	];

	if (!isLogged) {
		return <Navigate to={"/admin/auth/login"} replace={true} />;
	}

	return (
		<div>
			<PageTitle title="Back" />
			<div style={{ padding: "20px" }}>
				<Card>
					<div className="text-center mb-4">
						<Title level={2}>Performance Insights Dashboard</Title>
						<Text type="secondary">Comprehensive analytics and insights from performance data</Text>
					</div>

					{/* Key Metrics */}
					<Row gutter={16} className="mb-4">
						<Col span={6}>
							<Card>
								<Statistic
									title="Overall Avg Rating"
									value={insights.avgOverallRating}
									suffix="/ 5"
									prefix={<TrophyOutlined />}
									valueStyle={{ color: "#3f8600" }}
								/>
							</Card>
						</Col>
						<Col span={6}>
							<Card>
								<Statistic
									title="Total Reviews"
									value={insights.totalReviews}
									prefix={<CheckCircleOutlined />}
								/>
							</Card>
						</Col>
						<Col span={6}>
							<Card>
								<Statistic
									title="Review Completion"
									value={insights.reviewCompletionRate}
									suffix="%"
									valueStyle={{ color: "#1890ff" }}
								/>
							</Card>
						</Col>
						<Col span={6}>
							<Card>
								<Statistic
									title="Active PIPs"
									value={insights.activePIPs}
									prefix={<ExclamationCircleOutlined />}
									valueStyle={{ color: "#faad14" }}
								/>
							</Card>
						</Col>
					</Row>

					{/* Rating Distribution */}
					<Card title="Rating Distribution" className="mb-4">
						<Row gutter={16}>
							{Object.keys(insights.ratingDistribution).map((rating) => {
								const count = insights.ratingDistribution[rating];
								const total = Object.values(insights.ratingDistribution).reduce((a, b) => a + b, 0);
								const percentage = total > 0 ? (count / total) * 100 : 0;
								return (
									<Col span={4} key={rating}>
										<div style={{ textAlign: "center" }}>
											<Text strong>{rating}★</Text>
											<Progress
												type="circle"
												percent={percentage}
												format={() => count}
												size={80}
												strokeColor={
													rating >= 4 ? "#52c41a" : rating >= 3 ? "#1890ff" : "#faad14"
												}
											/>
										</div>
									</Col>
								);
							})}
						</Row>
					</Card>

					<Row gutter={16}>
						{/* Top Performers */}
						<Col span={12}>
							<Card
								title={
									<Space>
										<TrophyOutlined />
										Top Performers
									</Space>
								}
								className="mb-4"
							>
								<Table
									columns={topPerformersColumns}
									dataSource={insights.topPerformers}
									rowKey="employeeId"
									pagination={false}
									size="small"
								/>
							</Card>
						</Col>

						{/* Employees Needing Improvement */}
						<Col span={12}>
							<Card
								title={
									<Space>
										<WarningOutlined />
										Employees Needing Improvement
									</Space>
								}
								className="mb-4"
							>
								{insights.employeesNeedingImprovement.length > 0 ? (
									<Table
										columns={improvementNeededColumns}
										dataSource={insights.employeesNeedingImprovement}
										rowKey="employeeId"
										pagination={false}
										size="small"
									/>
								) : (
									<Alert
										message="No employees below threshold"
										description="All employees are performing above the minimum threshold."
										type="success"
										showIcon
									/>
								)}
							</Card>
						</Col>
					</Row>

					{/* Performance by Criteria */}
					<Card title="Performance by Criteria" className="mb-4">
						<Table
							columns={criteriaColumns}
							dataSource={criteriaData}
							rowKey="key"
							pagination={false}
						/>
					</Card>

					{/* Monthly Trends */}
					<Card title="Performance Trends (Last 6 Months)" className="mb-4">
						<Row gutter={16}>
							{insights.monthlyTrends.map((trend, index) => (
								<Col span={4} key={index}>
									<Card size="small">
										<Statistic
											title={trend.month}
											value={trend.average > 0 ? trend.average.toFixed(2) : "-"}
											suffix={trend.average > 0 ? "/5" : ""}
											valueStyle={{
												color: trend.average >= 4 ? "#3f8600" : trend.average >= 3 ? "#1890ff" : "#faad14",
											}}
										/>
										<Text type="secondary" style={{ fontSize: 12 }}>
											{trend.count} reviews
										</Text>
									</Card>
								</Col>
							))}
						</Row>
					</Card>

					{/* PIP Insights */}
					<Row gutter={16}>
						<Col span={12}>
							<Card title="PIP Statistics">
								<Space direction="vertical" style={{ width: "100%" }}>
									<Statistic
										title="PIP Success Rate"
										value={insights.pipSuccessRate}
										suffix="%"
										valueStyle={{ color: "#3f8600" }}
									/>
									<Progress
										percent={parseFloat(insights.pipSuccessRate)}
										status="active"
										strokeColor="#52c41a"
									/>
									<div style={{ marginTop: 16 }}>
										<Text>Active PIPs: {insights.activePIPs}</Text>
										<br />
										<Text>Completed PIPs: {insights.completedPIPs}</Text>
									</div>
								</Space>
							</Card>
						</Col>
						<Col span={12}>
							<Card title="Quick Insights">
								<Space direction="vertical" style={{ width: "100%" }}>
									<Alert
										message="Overall Performance"
										description={`Average rating of ${insights.avgOverallRating}/5 across all reviews`}
										type={parseFloat(insights.avgOverallRating) >= 4 ? "success" : parseFloat(insights.avgOverallRating) >= 3 ? "info" : "warning"}
										showIcon
									/>
									<Alert
										message="Review Coverage"
										description={`${insights.reviewCompletionRate}% of reviews are completed`}
										type={parseFloat(insights.reviewCompletionRate) >= 80 ? "success" : "warning"}
										showIcon
									/>
									{insights.activePIPs > 0 && (
										<Alert
											message="Active Improvement Plans"
											description={`${insights.activePIPs} employees are currently on PIPs`}
											type="info"
											showIcon
										/>
									)}
								</Space>
							</Card>
						</Col>
					</Row>
				</Card>
			</div>
		</div>
	);
};

export default PerformanceInsights;

